import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { PageShell } from "@/components/site/PageShell";
import { faqJsonLd } from "@/components/site/FAQAccordion";
import { BlogPostArticle, type PublishedBlogPost } from "@/components/site/BlogPostArticle";
import { seoMeta, canonicalLink, absoluteUrl, robotsMeta } from "@/lib/seo";
import { SITE_URL } from "@/lib/site";
import { type BlogPost } from "@/lib/blog";
import {
  getPublishedPostBySlug,
  getPublishedPostsBySlugList,
} from "@/server/blog/blog-public-detail.fns";
import { applyArticleOverride, type ArticleSchemaData } from "@/lib/seo-schema-apply";
import { blogImageAbsoluteUrl, blogImageBase, blogImageVariant } from "@/lib/blog-image";

export const Route = createFileRoute("/blog/$slug")({
  loader: async ({ params }) => {
    const post = await getPublishedPostBySlug({ data: { slug: params.slug } });
    if (!post) throw notFound();
    const related = await getPublishedPostsBySlugList({ data: { slugs: post.relatedPosts ?? [] } });
    return { post, related };
  },
  notFoundComponent: () => (
    <PageShell>
      <div className="mx-auto max-w-3xl px-5 py-32 text-center md:px-8">
        <h1 className="font-display text-4xl font-bold text-secondary">Artículo no encontrado</h1>
        <p className="mt-4 text-secondary/70">
          El artículo que buscas no existe o cambió de dirección.
        </p>
        <Link
          to="/blog"
          className="mt-8 inline-flex rounded-full bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground"
        >
          Ver todos los artículos
        </Link>
      </div>
    </PageShell>
  ),
  errorComponent: ({ error }) => (
    <PageShell>
      <div className="mx-auto max-w-3xl px-5 py-32 text-center md:px-8">
        <h1 className="font-display text-3xl font-bold text-secondary">
          Algo salió mal cargando el artículo
        </h1>
        <p className="mt-3 text-secondary/70">{error instanceof Error ? error.message : null}</p>
      </div>
    </PageShell>
  ),
  head: ({ loaderData, match }) => {
    const post = loaderData?.post;
    if (!post) return {};
    const url = `/blog/${post.slug}`;
    const override = match.context.seoOverride;
    return {
      meta: [
        ...seoMeta({
          title: override?.metaTitle ?? post.title,
          description: override?.metaDescription ?? post.metaDescription,
          path: url,
          type: "article",
          image: override?.ogImage ?? blogImageBase(post),
        }),
        ...robotsMeta(override?.robotsIndex ?? true, override?.robotsFollow ?? true),
        { property: "article:published_time", content: post.publishedAt },
        ...(post.updatedAt ? [{ property: "article:modified_time", content: post.updatedAt }] : []),
        { property: "article:section", content: post.category },
        { property: "article:author", content: "Improve Yourself" },
        ...[post.primaryKeyword, ...post.secondaryKeywords].map((kw) => ({
          property: "article:tag",
          content: kw,
        })),
      ],
      links: [
        ...canonicalLink(override?.canonicalPath ?? url),
        {
          rel: "preload",
          as: "image",
          href: blogImageVariant(blogImageBase(post), "700w"),
          type: "image/webp",
          fetchPriority: "high",
        },
      ],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(
            applyArticleOverride(
              blogPostingJsonLd(post),
              override?.schemaType === "article"
                ? (override.schemaData as Partial<ArticleSchemaData>)
                : null,
            ),
          ),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify(breadcrumbJsonLd(post)),
        },
        ...(post.faqs && post.faqs.length
          ? [
              {
                type: "application/ld+json",
                children: JSON.stringify(faqJsonLd(post.faqs)),
              },
            ]
          : []),
      ],
    };
  },
  component: BlogPostPage,
});

function blogPostingJsonLd(post: BlogPost) {
  const url = absoluteUrl(`/blog/${post.slug}`);
  const org = {
    "@type": "Organization",
    "@id": `${SITE_URL}/#organization`,
    name: "Improve Yourself",
    url: SITE_URL,
  };
  return {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "@id": url,
    headline: post.h1,
    description: post.metaDescription,
    inLanguage: "es-PE",
    datePublished: post.publishedAt,
    dateModified: post.updatedAt ?? post.publishedAt,
    author: org,
    publisher: org,
    mainEntityOfPage: url,
    articleSection: post.category,
    keywords: [post.primaryKeyword, ...post.secondaryKeywords].join(", "),
    image: {
      "@type": "ImageObject",
      url: blogImageAbsoluteUrl(blogImageBase(post)),
      width: post.imageWidth ?? 1200,
      height: post.imageHeight ?? 630,
    },
  };
}

function breadcrumbJsonLd(post: BlogPost) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Inicio", item: absoluteUrl("/") },
      { "@type": "ListItem", position: 2, name: "Blog", item: absoluteUrl("/blog") },
      { "@type": "ListItem", position: 3, name: post.h1, item: absoluteUrl(`/blog/${post.slug}`) },
    ],
  };
}

function BlogPostPage() {
  const { post, related } = Route.useLoaderData() as {
    post: PublishedBlogPost;
    related: BlogPost[];
  };

  return (
    <PageShell>
      <BlogPostArticle post={post} related={related} />
    </PageShell>
  );
}
